/**
 * Replacements for unmaintained `mui-file-dropzone` using `react-dropzone` + MUI.
 * Keeps similar props so call sites stay small.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

/** Map MIME types to react-dropzone `accept` entries (extension hints help OS file picker). */
function mimeListToAccept(mimes: string[]): Accept {
  const map: Accept = {};
  for (const mime of mimes) {
    if (mime === 'text/csv') {
      map[mime] = ['.csv'];
    } else if (mime === 'application/json') {
      map[mime] = ['.json'];
    } else {
      map[mime] = [];
    }
  }
  return map;
}

export type DropzoneAreaProps = {
  /** Controlled list of files (shown as chips when `showFileNames`). */
  fileObjects: File[];
  acceptedFiles: string[];
  maxFileSize: number;
  onChange: (files: File[]) => void;
  showFileNames?: boolean;
};

/**
 * Drop zone for step 1 of the Base import dialog (CSV).
 */
export function DropzoneArea({
  fileObjects,
  acceptedFiles,
  maxFileSize,
  onChange,
  showFileNames = false,
}: DropzoneAreaProps) {
  const theme = useTheme();
  const accept = useMemo(() => mimeListToAccept(acceptedFiles), [acceptedFiles]);
  // Parent state may lag behind rapid successive drops; read the latest list
  // from a ref so the second drop still sees files from the first.
  const fileObjectsRef = useRef(fileObjects);
  fileObjectsRef.current = fileObjects;

  const onDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      // Accumulate across separate drops (matches the old mui-file-dropzone
      // DropzoneArea), de-duping by name+size so re-dropping a file doesn't
      // add a duplicate chip.
      const merged = [...fileObjectsRef.current];
      for (const file of files) {
        const exists = merged.some(
          (f) => f.name === file.name && f.size === file.size
        );
        if (!exists) {
          merged.push(file);
        }
      }
      fileObjectsRef.current = merged;
      onChange(merged);
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept,
      maxSize: maxFileSize,
      onDrop,
      multiple: true,
    });

  const rejectionMsg = fileRejections[0]?.errors[0]?.message;

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: `2px dashed ${
            isDragActive ? theme.palette.primary.main : theme.palette.divider
          }`,
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive
            ? theme.palette.action.hover
            : theme.palette.background.paper,
          transition: theme.transitions.create(['background-color', 'border-color']),
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon
          sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          aria-hidden
        />
        <Typography variant="body1" gutterBottom>
          {isDragActive ? 'Drop files here…' : 'Drag & drop CSV files here'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse (max {(maxFileSize / (1024 * 1024)).toFixed(0)} MB)
        </Typography>
      </Box>
      {rejectionMsg ? (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {rejectionMsg}
        </Typography>
      ) : null}
      {showFileNames && fileObjects.length > 0 ? (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {fileObjects.map((f) => (
            <Chip key={`${f.name}-${f.size}`} size="small" label={f.name} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export type DropzoneDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen files when the user confirms (Submit). */
  onChange: (files: File[]) => void;
  acceptedFiles: string[];
  filesLimit?: number;
  maxFileSize: number;
  /** Kept for API compatibility; previews are not implemented. */
  showPreviews?: boolean;
  /** Label for the confirm button (matches mui-file-dropzone's submit flow). */
  submitButtonText?: string;
};

/**
 * Modal file picker (e.g. Flow JSON upload). Mirrors the old mui-file-dropzone
 * confirm-then-save flow: dropped files are staged and shown, and only emitted
 * via `onChange` when the user clicks Submit (Cancel/close discards them).
 */
export function DropzoneDialog({
  open,
  onClose,
  onChange,
  acceptedFiles,
  filesLimit = 1,
  maxFileSize,
  submitButtonText = 'Submit',
}: DropzoneDialogProps) {
  const theme = useTheme();
  const accept = useMemo(() => mimeListToAccept(acceptedFiles), [acceptedFiles]);
  const [staged, setStaged] = useState<File[]>([]);

  // Clear the staged selection whenever the dialog closes so a previous pick
  // never leaks into the next time it's opened.
  useEffect(() => {
    if (!open) {
      setStaged([]);
    }
  }, [open]);

  const onDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      const picked =
        filesLimit <= 1 ? files.slice(0, 1) : files.slice(0, filesLimit);
      setStaged(picked);
    },
    [filesLimit]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept,
      maxSize: maxFileSize,
      maxFiles: filesLimit,
      onDrop,
      multiple: filesLimit > 1,
    });

  const rejectionMsg = fileRejections[0]?.errors[0]?.message;

  const handleClose = () => {
    setStaged([]);
    onClose();
  };

  const handleSubmit = () => {
    if (staged.length === 0) {
      return;
    }
    onChange(staged);
    // Mirror mui-file-dropzone: auto-close after save and clear the staged
    // selection so a second click can't re-emit the same files.
    setStaged([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload file</DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          sx={{
            border: `2px dashed ${
              isDragActive ? theme.palette.primary.main : theme.palette.divider
            }`,
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            mt: 1,
            bgcolor: isDragActive
              ? theme.palette.action.hover
              : theme.palette.background.paper,
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body1" gutterBottom>
            Drop a file here or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max {(maxFileSize / (1024 * 1024)).toFixed(1)} MB
          </Typography>
        </Box>
        {rejectionMsg ? (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
            {rejectionMsg}
          </Typography>
        ) : null}
        {staged.length > 0 ? (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {staged.map((f) => (
              <Chip
                key={`${f.name}-${f.size}`}
                size="small"
                label={f.name}
                onDelete={() =>
                  setStaged((prev) => prev.filter((file) => file !== f))
                }
              />
            ))}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={staged.length === 0}
        >
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
